/**
 * Pure, unit-tested state logic for the Audit Console (AuditExplorer):
 * date presets, custom ranges, URL state (parse/serialize), Status-view detection,
 * status/event-type selection coupling, Top-people aggregation, facet summary and
 * CSV truncation detection. The browser helpers at the bottom are thin, best-effort
 * wrappers over window.location / history.replaceState.
 */
import { startOfDay, endOfDay } from 'date-fns';
import type { AuditActorFacet, AuditEventFacets } from '@/types';
import { STATUS_CHANGED_EVENT, actorFacetKey } from './auditMeta';

// ---------------------------------------------------------------------------------------
// Date presets
// ---------------------------------------------------------------------------------------

export type RollingPreset = '1d' | '7d' | '30d' | '90d' | 'all';
export type AuditPreset = RollingPreset | 'custom';

export const TIME_PRESETS: Array<{ label: string; value: AuditPreset }> = [
  { label: 'Last 24 hours', value: '1d' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'All time', value: 'all' },
  { label: 'Custom range…', value: 'custom' },
];

const MINUTE_MS = 60 * 1000;
const DAY_MS = 24 * 60 * MINUTE_MS;

const PRESET_SPAN_MS: Record<string, number> = {
  '1d': DAY_MS,
  '7d': 7 * DAY_MS,
  '30d': 30 * DAY_MS,
  '90d': 90 * DAY_MS,
};

export function isAuditPreset(v: unknown): v is AuditPreset {
  return typeof v === 'string' && TIME_PRESETS.some((p) => p.value === v);
}

export function isRollingPreset(v: unknown): v is RollingPreset {
  return isAuditPreset(v) && v !== 'custom';
}

/**
 * Honest rolling windows: '1d' = now − 24h, '7d' = now − 7 days, '30d', '90d'; 'all' = none.
 * `from` is rounded DOWN to the minute so the query key is stable within a minute.
 * `to` is omitted (open-ended up to now).
 */
export function presetToRange(preset: string, now: Date = new Date()): { from?: string; to?: string } {
  const span = PRESET_SPAN_MS[preset];
  if (!span) return {};
  const fromMs = Math.floor((now.getTime() - span) / MINUTE_MS) * MINUTE_MS;
  return { from: new Date(fromMs).toISOString() };
}

const DATE_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar date in `<input type="date">` form (yyyy-MM-dd). */
export function isDateInput(v: unknown): v is string {
  if (typeof v !== 'string') return false;
  const m = DATE_INPUT_RE.exec(v);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(y, mo - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
}

export type CustomRangeStatus = 'ok' | 'incomplete' | 'invalid';

/** A custom range applies only when BOTH dates are filled, valid, and in order. */
export function customRangeStatus(from: string, to: string): CustomRangeStatus {
  if (!from || !to) return 'incomplete';
  if (!isDateInput(from) || !isDateInput(to)) return 'invalid';
  if (from > to) return 'invalid'; // yyyy-MM-dd compares lexically
  return 'ok';
}

/** Local-day bounds of a complete custom range (start of `from` → end of `to`). */
export function customRange(from: string, to: string): { from: string; to: string } {
  const f = new Date(`${from}T00:00:00`);
  const t = new Date(`${to}T00:00:00`);
  return { from: startOfDay(f).toISOString(), to: endOfDay(t).toISOString() };
}

/**
 * The range actually sent to the server. 'custom' uses the last COMPLETE custom pair; until one
 * exists it keeps the previously applied rolling preset (the console never goes unbounded or
 * half-bounded just because one date box is empty).
 */
export function resolveRange(
  args: { preset: AuditPreset; lastRolling: RollingPreset; appliedCustom: { from: string; to: string } | null },
  now: Date = new Date()
): { from?: string; to?: string } {
  if (args.preset !== 'custom') return presetToRange(args.preset, now);
  if (args.appliedCustom) return customRange(args.appliedCustom.from, args.appliedCustom.to);
  return presetToRange(args.lastRolling, now);
}

// ---------------------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------------------

export interface AuditUrlState {
  range: AuditPreset;
  /** Custom-range date inputs (yyyy-MM-dd); only serialized when range === 'custom'. */
  from: string;
  to: string;
  eventTypes: string[];
  statuses: string[];
  /** Actor key ('u:<id>' | 'e:<email>' | 'system' | 'anon'). */
  actor: string | null;
  clientId: string | null;
  categories: string[];
  q: string;
  /** Deposit drill-down (entityType 'deposit', entityId). */
  depositId: string | null;
}

/**
 * Query-param names. `q` and `deposit` are NAMESPACED (`aq`, `adeposit`) because the Deposits
 * page already owns `?q=` (its quick search) and `?deposit=` (opens the allocation modal — see
 * utils/modalParam.ts). Sharing those names would make an audit link open a deposit's
 * allocation modal (and write a deposit.viewed audit row) and would clobber the page's search.
 */
export const AUDIT_URL_KEYS = {
  flag: 'audit',
  range: 'range',
  from: 'from',
  to: 'to',
  type: 'type',
  status: 'status',
  actor: 'actor',
  client: 'client',
  cat: 'cat',
  q: 'aq',
  deposit: 'adeposit',
} as const;

export const AUDIT_URL_PARAMS: readonly string[] = Object.values(AUDIT_URL_KEYS);

/** Default view on open (no URL state): last 24h of status changes, all clients, all people. */
export function defaultAuditState(): AuditUrlState {
  return {
    range: '1d',
    from: '',
    to: '',
    eventTypes: [STATUS_CHANGED_EVENT],
    statuses: [],
    actor: null,
    clientId: null,
    categories: [],
    q: '',
    depositId: null,
  };
}

function parseCsvList(v: string | null): string[] {
  if (!v) return [];
  const out: string[] = [];
  for (const raw of v.split(',')) {
    const s = raw.trim();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

/** Parse the console state from a query string. Returns null unless `audit=1` is present. */
export function parseAuditUrlState(search: string): AuditUrlState | null {
  const p = new URLSearchParams(search);
  if (p.get(AUDIT_URL_KEYS.flag) !== '1') return null;
  const rangeRaw = p.get(AUDIT_URL_KEYS.range);
  const range: AuditPreset = isAuditPreset(rangeRaw) ? rangeRaw : '1d';
  const from = p.get(AUDIT_URL_KEYS.from) ?? '';
  const to = p.get(AUDIT_URL_KEYS.to) ?? '';
  return {
    range,
    from: range === 'custom' && isDateInput(from) ? from : '',
    to: range === 'custom' && isDateInput(to) ? to : '',
    eventTypes: parseCsvList(p.get(AUDIT_URL_KEYS.type)),
    statuses: parseCsvList(p.get(AUDIT_URL_KEYS.status)),
    actor: p.get(AUDIT_URL_KEYS.actor) || null,
    clientId: p.get(AUDIT_URL_KEYS.client) || null,
    categories: parseCsvList(p.get(AUDIT_URL_KEYS.cat)),
    q: (p.get(AUDIT_URL_KEYS.q) ?? '').trim(),
    depositId: p.get(AUDIT_URL_KEYS.deposit) || null,
  };
}

/** Initial console state: from the URL when `audit=1` is present, else the default view. */
export function initialAuditState(search: string): AuditUrlState {
  return parseAuditUrlState(search) ?? defaultAuditState();
}

/**
 * An `e:<email>` actor key carries an email address. frontend/CLAUDE.md forbids PII in URL
 * params (URLs land in history, access logs and referrers), so it is never written to the URL.
 */
export function isUrlSafeActorKey(key: string | null | undefined): key is string {
  return !!key && !key.startsWith('e:');
}

/**
 * Free-text search can match users' email addresses server-side, so a search containing '@'
 * may itself be an email — it is never written to the URL (same PII rule as the actor key).
 */
export function isUrlSafeSearch(q: string | null | undefined): q is string {
  return !!q && !!q.trim() && !q.includes('@');
}

/** Remove every audit param from a query string, preserving unrelated params. */
export function stripAuditUrlParams(search: string): string {
  const p = new URLSearchParams(search);
  for (const k of AUDIT_URL_PARAMS) p.delete(k);
  return p.toString();
}

/**
 * Write the console state into a query string (returned without the leading '?'),
 * preserving unrelated params. Empty filters are omitted.
 */
export function serializeAuditUrlState(state: AuditUrlState, baseSearch = ''): string {
  const p = new URLSearchParams(stripAuditUrlParams(baseSearch));
  p.set(AUDIT_URL_KEYS.flag, '1');
  p.set(AUDIT_URL_KEYS.range, state.range);
  if (state.range === 'custom') {
    if (state.from) p.set(AUDIT_URL_KEYS.from, state.from);
    if (state.to) p.set(AUDIT_URL_KEYS.to, state.to);
  }
  if (state.eventTypes.length) p.set(AUDIT_URL_KEYS.type, state.eventTypes.join(','));
  if (state.statuses.length) p.set(AUDIT_URL_KEYS.status, state.statuses.join(','));
  if (isUrlSafeActorKey(state.actor)) p.set(AUDIT_URL_KEYS.actor, state.actor);
  if (state.clientId) p.set(AUDIT_URL_KEYS.client, state.clientId);
  if (state.categories.length) p.set(AUDIT_URL_KEYS.cat, state.categories.join(','));
  if (isUrlSafeSearch(state.q)) p.set(AUDIT_URL_KEYS.q, state.q.trim());
  if (state.depositId) p.set(AUDIT_URL_KEYS.deposit, state.depositId);
  return p.toString();
}

// Browser wrappers — merge from the LIVE window.location (react-router never observes raw
// history writes; see utils/modalParam.ts) and keep history.state so router bookkeeping survives.

function replaceSearch(search: string): void {
  const nextUrl = window.location.pathname + (search ? `?${search}` : '') + window.location.hash;
  if (nextUrl === window.location.pathname + window.location.search + window.location.hash) return;
  window.history.replaceState(window.history.state, '', nextUrl);
}

/** Mirror the console state into the page URL (best-effort; never throws). */
export function writeAuditUrlState(state: AuditUrlState): void {
  try {
    replaceSearch(serializeAuditUrlState(state, window.location.search));
  } catch {
    /* URL/history unavailable — link persistence is best-effort */
  }
}

/** Remove the audit params from the page URL (best-effort; never throws). */
export function clearAuditUrlParams(): void {
  try {
    replaceSearch(stripAuditUrlParams(window.location.search));
  } catch {
    /* best-effort */
  }
}

/** True when the page URL asks for the audit console (`audit=1`). */
export function hasAuditUrlFlag(): boolean {
  try {
    return new URLSearchParams(window.location.search).get(AUDIT_URL_KEYS.flag) === '1';
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------------------
// Selection logic
// ---------------------------------------------------------------------------------------

/** Status view = event type is exactly ['deposit.status_changed'] OR any status filter is active. */
export function isStatusView(eventTypes: readonly string[] | undefined, statuses: readonly string[] | undefined): boolean {
  if ((statuses?.length ?? 0) > 0) return true;
  return eventTypes?.length === 1 && eventTypes[0] === STATUS_CHANGED_EVENT;
}

export interface TypeStatusSelection {
  eventTypes: string[];
  statuses: string[];
}

/**
 * Toggle one status. SELECTING a status also narrows the event type to exactly
 * ['deposit.status_changed'] (statuses only exist on those rows); removing one leaves it alone.
 */
export function toggleStatusSelection(cur: TypeStatusSelection, status: string): TypeStatusSelection {
  const selecting = !cur.statuses.includes(status);
  const statuses = selecting ? [...cur.statuses, status] : cur.statuses.filter((s) => s !== status);
  const exactlyStatus = cur.eventTypes.length === 1 && cur.eventTypes[0] === STATUS_CHANGED_EVENT;
  const eventTypes = selecting && !exactlyStatus ? [STATUS_CHANGED_EVENT] : cur.eventTypes;
  return { eventTypes, statuses };
}

/**
 * Toggle one event type. Statuses are left untouched: the server applies the status condition
 * only to status-change rows, so statuses + other ticked types are sent together as-is.
 */
export function toggleEventTypeSelection(cur: TypeStatusSelection, eventType: string): TypeStatusSelection {
  const eventTypes = cur.eventTypes.includes(eventType)
    ? cur.eventTypes.filter((t) => t !== eventType)
    : [...cur.eventTypes, eventType];
  return { eventTypes, statuses: cur.statuses };
}

// ---------------------------------------------------------------------------------------
// Top people (distribution bar)
// ---------------------------------------------------------------------------------------

export interface PeopleBreakdown {
  /** Top N by count (desc), plus the active person if they fall outside the top N. */
  top: AuditActorFacet[];
  /** Everyone else, aggregated (null when nobody is left over). */
  others: { people: number; count: number } | null;
  /** Sum of `count` over ALL people — the block's own total. */
  total: number;
}

/**
 * Top-N people + an "Others" aggregate so the segments sum exactly to the block total.
 * Entries missing a server `key` get one derived with the same scheme.
 */
export function buildTopPeople(
  byActor: readonly AuditActorFacet[] | undefined,
  activeKey: string | null,
  limit = 8
): PeopleBreakdown {
  const all = (byActor ?? [])
    .map((a) => ({ ...a, key: actorFacetKey(a), count: a.count || 0 }))
    .sort((a, b) => b.count - a.count);
  const top = all.slice(0, limit);
  if (activeKey && !top.some((a) => a.key === activeKey)) {
    const active = all.find((a) => a.key === activeKey);
    if (active) top.push(active);
  }
  const topKeys = new Set(top.map((a) => a.key));
  const rest = all.filter((a) => !topKeys.has(a.key));
  const total = all.reduce((sum, a) => sum + a.count, 0);
  const othersCount = rest.reduce((sum, a) => sum + a.count, 0);
  return {
    top,
    others: rest.length ? { people: rest.length, count: othersCount } : null,
    total,
  };
}

// ---------------------------------------------------------------------------------------
// Summary + export
// ---------------------------------------------------------------------------------------

function plural(n: number, word: string): string {
  return `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
}

/** Header line: status view → "N changes · M deposits"; otherwise → "N events". Null when unknown. */
export function facetSummary(
  facets: Pick<AuditEventFacets, 'total' | 'deposits'> | undefined | null,
  statusView: boolean
): string | null {
  if (!facets) return null;
  const total = facets.total ?? 0;
  if (statusView) return `${plural(total, 'change')} · ${plural(facets.deposits ?? 0, 'deposit')}`;
  return plural(total, 'event');
}

/**
 * True when the export hit the server's row cap: the LAST line starts with `"TRUNCATED`.
 * Accepts the whole CSV or just its tail.
 */
export function csvIsTruncated(text: string): boolean {
  const trimmed = text.replace(/[\r\n]+$/, '');
  let lastLine = trimmed.slice(trimmed.lastIndexOf('\n') + 1);
  if (lastLine.charCodeAt(0) === 0xfeff) lastLine = lastLine.slice(1); // byte-order mark (tail = whole file)
  return lastLine.startsWith('"TRUNCATED');
}

/** Browser IANA time zone for the export's local-time column (undefined if unavailable). */
export function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}
